import { useMemo } from 'react';

import { api, CareServiceOrgansKey } from '@/api';
import { FindingsByOrgan } from '@/shared/model';
import { categorizeFindings, groupFindingsByOrgan } from '@/shared/util';

const EMPTY_FINDINGS_BY_ORGAN = {} as FindingsByOrgan;

interface UseReportFindingsOptions {
  enabled?: boolean;
}

export function useReportFindings(
  reportUuid: string | undefined,
  { enabled = true }: UseReportFindingsOptions = {},
) {
  const { data: renderedSummaries, isLoading } =
    api.fct.renderedSummary.query.useGetReportRenderedSummaries(
      { payload: { uuid: reportUuid } },
      { enabled: enabled && Boolean(reportUuid) },
    );

  const { defaultFindings, findings } = useMemo(() => {
    if (!renderedSummaries?.length) {
      return {
        defaultFindings: EMPTY_FINDINGS_BY_ORGAN,
        findings: EMPTY_FINDINGS_BY_ORGAN,
      };
    }

    const groupedData = groupFindingsByOrgan(renderedSummaries);
    const categorizedFindings = {
      defaultFindings: {} as FindingsByOrgan,
      findings: {} as FindingsByOrgan,
    };

    Object.keys(groupedData).forEach((organ) => {
      const { commonFindings, defaultFindings: defaultFindingsData } = categorizeFindings(
        groupedData[organ as CareServiceOrgansKey],
      );

      categorizedFindings.findings[organ as CareServiceOrgansKey] = commonFindings;
      categorizedFindings.defaultFindings[organ as CareServiceOrgansKey] = defaultFindingsData;
    });

    return categorizedFindings;
  }, [renderedSummaries]);

  return useMemo(
    () => ({
      defaultFindings,
      findings,
      isLoading,
    }),
    [defaultFindings, findings, isLoading],
  );
}
